import { useCallback, useEffect, useRef, useState } from 'react'
import { knowledgeApi, type ErasureReceipt } from '../../services/api'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { workspaceService } from '../../services/workspaceService'
import { isStaleRoleRefusal } from '../../utils/permissions'
import { useWorkspaceRole } from '../../utils/useWorkspaceRole'
import { refusalFor } from './refusal'
import { copyFor, historyWord, readingOf, withheldNotes } from './erasureReceipt'
import {
  Alert,
  Button,
  Card,
  CardFooter,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Eyebrow,
  List,
  LoadingState,
  Meta,
} from '../../ds'

/**
 * Forget one person, in a single action (App IA v5 phase 5b, MAR-204 PR 2).
 *
 * ── Why this is not a button ────────────────────────────────────────────────────────────
 *
 * `docs/brand/product-tests.md` lists **single-action per-customer erasure** in the floor —
 * a gate, not a trade-off. Phase 5a built the door and nothing in the web app reached it,
 * so until now the published claim was true only for an operator with AWS access. This
 * component is what makes it a merchant action.
 *
 * ── What the design specified, and what it could not ────────────────────────────────────
 *
 * v6 draws the control — a section in the person drawer, arm-then-confirm —
 * and then fires one unconditional toast: *"Erased in one action — every observation, every
 * inference, and the conversations behind them."* The interaction is taken as drawn. **That
 * string is not shipped**, for two independent reasons:
 *
 *  1. It is fired on the response to a **202**. The door accepts the work; it has not done
 *     it. Twenty steps over ~20 tables cannot finish inside API Gateway's non-overridable
 *     29-second ceiling, which is why the receipt exists.
 *  2. Even once the engine reports, the **common** answer is `needs_review`, which is
 *     terminal, is not a failure, and does not support a completeness claim. See
 *     `erasureReceipt.ts`, which holds the whole of that judgment.
 *
 * ⚠️ **Two acts, still.** Arming opens the design system's `ConfirmDialog` (MAR-372) — the
 * same two steps the inline arm had: "Forget this person entirely", then the danger button
 * naming who. Cancel (or Escape) disarms; nothing is sent until the second act, the dialog
 * cannot be dismissed while the request is in flight, and every refusal disarms it. What
 * came back is never in the dialog — it is the receipt below, on the drawer.
 *
 * ── Who sees it ─────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ **This component is NOT the gate.** `assertWorkspaceRole` runs server-side on every
 * request, before any read, and refuses `member` with a 403 whatever this file renders.
 * The erasure route was the first place the platform enforced this for real; MAR-170 applies
 * the same shared resolver to workspace updates. Hiding the control is courtesy — not showing
 * someone an act they cannot perform — and the 403 copy below handles a stale client-side role.
 *
 * ⚠️ It offers the control only on a role that can use it — `owner`, `admin` or
 * `super_admin` in THIS workspace. A caller with no membership row here has no role: since
 * MAR-250 `assertWorkspaceRole` has no rosterless-owner fallback and refuses them with
 * `no_workspace_membership`, so offering them the act would be a dead end. (It used to err
 * open on "no role found" for Shopify-install merchants, who had no row; the install now
 * writes their `owner` row.)
 *
 * ── Scoping ─────────────────────────────────────────────────────────────────────────────
 *
 * Every cached value names the workspace AND the customer it describes and is matched at
 * the READ, exactly as `CustomerKnowledge` does it: React renders once with the new props
 * and the old state before any effect runs, and here that frame would offer to erase one
 * person under another's name. The role is looked up BY the `workspaceId` prop through
 * `useWorkspaceRole({ workspaceId })` (the one role hook, shared with TeamView): the membership row for that workspace, and
 * nothing else. `currentUser` is never read — it is a "current workspace" value that can
 * describe another workspace, and it outlived a departed owner's row (MAR-250 review round
 * 2). The post-create window is covered by the created row being merged into the membership.
 *
 * ⚠️ A 403 whose `details.roleReason` says the caller's role here is stale
 * (`no_workspace_membership` — removed; `insufficient_workspace_role` — demoted) refreshes
 * the membership, so the control stops being offered rather than refusing again on every
 * click.
 */

/** The roles `assertWorkspaceRole` lets through to the erasure door. */
const ERASURE_ROLES: ReadonlySet<string> = new Set(['owner', 'admin', 'super_admin'])

/** How often the receipt is re-read while the engine is working. */
const ERASURE_POLL_MS = 3000
/**
 * How many polls before this panel stops watching.
 *
 * 40 × 3s ≈ two minutes. It is a watching budget, not a timeout: nothing is cancelled when
 * it runs out, and the copy says so. A panel that polled forever would keep a tab calling a
 * Lambda all afternoon for an answer the merchant stopped waiting for.
 */
const ERASURE_POLL_BUDGET = 40

/** How many receipts the history affordance asks for. Server clamps 1–100. */
const HISTORY_LIMIT = 20

/** One erasure this panel is watching, stamped with the record it belongs to. */
interface Flow {
  workspaceId: string
  customerId: string
  requestId: string
  /** `null` until the first receipt read lands — accepted is not the same as reported. */
  receipt: ErasureReceipt | null
  polls: number
  /** The budget ran out. The work did not stop; this panel stopped watching it. */
  unwatched: boolean
  /** A receipt READ refused. The erasure itself is unaffected, and the copy says which. */
  pollRefusal?: string
}

/** A refusal of the erasure request itself, scoped to the record it was fired on. */
interface Note {
  workspaceId: string
  customerId: string
  text: string
}

/** The in-flight erasure a 409 pointed at, so it can be watched instead of duplicated. */
interface Offer {
  workspaceId: string
  customerId: string
  requestId: string
  status: string
}

/** The workspace's receipts, stamped with the workspace they describe. */
interface History {
  workspaceId: string
  receipts: ErasureReceipt[]
  /**
   * ⚠️ Three-valued, and `null` is load-bearing: it means **the server did not say**.
   *
   * "That is every erasure this workspace has run" is a completeness claim on an audit
   * trail, and it has to be the SERVER's claim. Coercing a missing field to `false` would
   * make the page assert completeness on a response that asserted nothing — the same
   * inference-instead-of-a-statement this route exists to avoid.
   */
  truncated: boolean | null
  refusal?: string
}

const scopeKey = (workspaceId: string, customerId: string) => `${workspaceId}::${customerId}`

/** A date a merchant can read, or the raw stamp if it is not one. */
function when(iso: string | null | undefined): string {
  if (!iso) return ''
  const t = new Date(iso)
  if (!Number.isFinite(t.getTime())) return ''
  return t.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

export default function CustomerErasure({
  workspaceId,
  customerId,
  emailMasked,
  pollMs = ERASURE_POLL_MS,
  pollBudget = ERASURE_POLL_BUDGET,
}: {
  workspaceId?: string
  customerId?: string
  /**
   * `a••@example.com`, from the knowledge read's `person`.
   *
   * ⚠️ It is masked rather than redacted **precisely so the confirmation can name a
   * person** (§9.1) — a confirmation that cannot distinguish two people is a confirmation
   * of nothing. `null` when that read has not landed or the record holds no email; the
   * confirmation then names the record's own id, which is exact even when it is ugly.
   */
  emailMasked?: string | null
  /** Test seam. The shipped values are the exported constants; the drawer passes neither. */
  pollMs?: number
  pollBudget?: number
}) {
  const { userMembership } = useWorkspaceStore()
  // The ONE role hook (PR #237 round 5), scoped to the `workspaceId` PROP — not the current
  // workspace — so the role is asked about exactly the workspace this erasure would act on.
  // Called here, above the early return below, because it is a hook.
  const { role } = useWorkspaceRole({ workspaceId })
  const [armedFor, setArmedFor] = useState<string | null>(null)
  const [sendingFor, setSendingFor] = useState<string | null>(null)
  const [flowState, setFlowState] = useState<Flow | null>(null)
  const [noteState, setNoteState] = useState<Note | null>(null)
  const [offerState, setOfferState] = useState<Offer | null>(null)
  const [historyState, setHistoryState] = useState<History | null>(null)
  const [historyOpenFor, setHistoryOpenFor] = useState<string | null>(null)

  const key = workspaceId && customerId ? scopeKey(workspaceId, customerId) : null

  // ── Scoped at the read. A value describing another record is simply not current. ──
  const armed = key != null && armedFor === key
  const sending = key != null && sendingFor === key
  const flow =
    flowState && flowState.workspaceId === workspaceId && flowState.customerId === customerId
      ? flowState
      : null
  const note =
    noteState && noteState.workspaceId === workspaceId && noteState.customerId === customerId
      ? noteState
      : null
  const offer =
    offerState && offerState.workspaceId === workspaceId && offerState.customerId === customerId
      ? offerState
      : null
  // History is a WORKSPACE fact, so it is scoped by workspace alone — naming the customer
  // in its key would re-fetch the same list for every person the merchant opened.
  const historyOpen = workspaceId != null && historyOpenFor === workspaceId
  const history =
    historyState && historyState.workspaceId === workspaceId ? historyState : null

  /**
   * Per-request-id poll generation.
   *
   * ⚠️ **The `requestId` check below does not cover two polls for the SAME id, and they
   * happen.** "Check again" calls `watch`, which rebuilds the flow and restarts the loop
   * while the previous poll may still be in flight — both carry the same `requestId`, so
   * both pass that guard, and if the older one lands last it overwrites a newer receipt and
   * double-counts `polls`. Found while fixing the history's version of this; same shape,
   * same fix.
   */
  const pollSeq = useRef<Record<string, number>>({})

  const pollOnce = useCallback(async(ws: string, requestId: string) => {
    const seq = (pollSeq.current[requestId] = (pollSeq.current[requestId] ?? 0) + 1)
    const res = await knowledgeApi.getErasureReceipt(ws, requestId)
    if (pollSeq.current[requestId] !== seq) return
    setFlowState((prev) => {
      // Only the flow this poll was fired for may write. A late answer for an erasure the
      // merchant has already replaced must not resurrect it.
      if (!prev || prev.requestId !== requestId) return prev
      if (res.success && res.data?.receipt) {
        return { ...prev, receipt: res.data.receipt, polls: prev.polls + 1, pollRefusal: undefined }
      }
      // ⚠️ A failed READ is not a failed erasure, and the two must not be shown as one
      // thing. The last receipt we did get stays on screen underneath this.
      return {
        ...prev,
        polls: prev.polls + 1,
        pollRefusal: refusalFor(res, 'Could not read the receipt just now.', 'erasure'),
      }
    })
  }, [])

  /**
   * The polling loop.
   *
   * ⚠️ It runs on `flow`, which is the SCOPED value — so switching customer pauses the loop
   * rather than leaving it polling under someone else's name, and switching back resumes
   * it. Closing the drawer unmounts this component entirely (`KnowledgeDrawer.tsx` renders the
   * customer body only while a customer is shown), which loses the watch but not the
   * erasure: the 409 offer below and the history list are both routes back to it, and every
   * give-up sentence says the work continues on our side.
   *
   * ⚠️ It stops at `terminal`, which includes `failed` — but `failed` is not over, so that
   * render offers "Check again" rather than a result. See `erasureReceipt.ts`.
   */
  useEffect(() => {
    if (!flow || flow.unwatched) return
    if (flow.receipt?.terminal) return
    if (flow.polls >= pollBudget) {
      const id = flow.requestId
      setFlowState((prev) => (prev && prev.requestId === id ? { ...prev, unwatched: true } : prev))
      return
    }
    const ws = flow.workspaceId
    const id = flow.requestId
    const timer = setTimeout(() => { void pollOnce(ws, id) }, pollMs)
    return () => clearTimeout(timer)
  }, [flow, pollMs, pollBudget, pollOnce])

  /** Watch a request by id — the 409's offer, and the "check again" on a give-up. */
  const watch = useCallback((ws: string, cust: string, requestId: string) => {
    setOfferState(null)
    setNoteState(null)
    setFlowState({ workspaceId: ws, customerId: cust, requestId, receipt: null, polls: 0, unwatched: false })
  }, [])

  /**
   * Read the workspace's receipts.
   *
   * ⚠️ **Sequence-guarded, and the workspace check does NOT cover this.** Open the list,
   * close it, reopen it: two reads are in flight for the SAME workspace, so every
   * scope test they both pass. If the first returns last it wins, and an older list — with
   * an older `truncated` claim on it — overwrites the newer one. The dimension that tells
   * them apart is *which read*, not which workspace, which is the lesson `beliefCounts.ts`
   * and PR 1's read-identity round already paid for. Same `requestSeq` shape as
   * `CustomerKnowledge.tsx`, keyed per workspace so two workspaces do not cancel each other.
   */
  const historySeq = useRef<Record<string, number>>({})

  const loadHistory = useCallback(async(ws: string) => {
    const seq = (historySeq.current[ws] = (historySeq.current[ws] ?? 0) + 1)
    const res = await knowledgeApi.listErasures(ws, HISTORY_LIMIT)
    // A newer read for this workspace has been asked for; this answer is already history.
    if (historySeq.current[ws] !== seq) return
    if (res.success && res.data) {
      setHistoryState({
        workspaceId: ws,
        receipts: res.data.receipts || [],
        // ⚠️ Stated by the server, never inferred from the length, and never coerced: a
        // response that omits it leaves this `null`, which says "unknown" rather than
        // "complete". A list that quietly looks complete is the opposite of an audit trail.
        truncated: typeof res.data.truncated === 'boolean' ? res.data.truncated : null,
      })
      return
    }
    setHistoryState({
      workspaceId: ws, receipts: [], truncated: null,
      refusal: refusalFor(res, 'Could not read this workspace\'s erasures.', 'erasure'),
    })
  }, [])

  /**
   * The held history no longer describes the workspace — drop it, and re-read it if the
   * merchant is looking at it.
   *
   * ⚠️ Last round's fix re-read on OPEN, which does nothing for a list that is **already
   * open**. Fire an erasure underneath one showing `truncated: false` and it keeps saying
   * *"That is every erasure this workspace has run"* while missing the one just started —
   * a false completeness claim on an audit trail, which is the thing this list must never
   * make. Clearing alone is not enough either: nothing else re-fetches, so an open panel
   * would sit on "reading…" forever.
   */
  const refreshHistory = useCallback((ws: string) => {
    setHistoryState(null)
    if (historyOpenFor === ws) void loadHistory(ws)
  }, [historyOpenFor, loadHistory])

  const fire = useCallback(async() => {
    if (!workspaceId || !customerId) return
    const ws = workspaceId
    const cust = customerId
    // Who this request is issued as. A stale-role refusal is about THAT user; if another is
    // signed in by the time it arrives, `refreshMembership` leaves their rows alone.
    const askedAs = userMembership?.clerkUserId
    setSendingFor(scopeKey(ws, cust))
    setNoteState(null)
    setOfferState(null)
    try {
      const res = await knowledgeApi.requestErasure(ws, cust)
      if (res.success && res.data?.requestId) {
        // ⚠️ 202. Nothing is claimed here beyond "she has started" — the receipt is what
        // says what happened, and the render below waits for it rather than announcing it.
        setArmedFor(null)
        setFlowState({
          workspaceId: ws, customerId: cust, requestId: res.data.requestId,
          receipt: null, polls: 0, unwatched: false,
        })
        // ⚠️ The workspace's history is now one erasure longer, and a list already open
        // is showing a set that no longer exists — including, if it said so, that it was
        // every erasure this workspace had run.
        refreshHistory(ws)
        return
      }
      // The caller's role here is not what this panel believed: re-read the membership so the
      // control stops being offered. The note below still says why this one was refused.
      if (isStaleRoleRefusal(res)) {
        void workspaceService.refreshMembership({ refusedClerkUserId: askedAs })
      }
      const details = res.details as { reason?: unknown; requestId?: string; status?: string } | undefined
      if (details?.reason === 'erasure_already_in_flight' && details.requestId) {
        // What the merchant asked for is already happening. Offering the one that is
        // running is a better answer than a second request against an idempotent engine.
        setOfferState({
          workspaceId: ws, customerId: cust,
          requestId: details.requestId, status: details.status || 'running',
        })
      }
      setNoteState({
        workspaceId: ws, customerId: cust,
        text: refusalFor(res, 'Nothing has been erased.', 'erasure'),
      })
      // Disarmed on every refusal. A live confirm button left sitting under a refusal is
      // one stray click from a second attempt the merchant did not decide to make.
      setArmedFor(null)
    } finally {
      setSendingFor((prev) => (prev === scopeKey(ws, cust) ? null : prev))
    }
  }, [workspaceId, customerId, refreshHistory, userMembership?.clerkUserId])

  /**
   * A flow that has settled changes what the history would say about it — its status word
   * moves from "Running" to one of the terminal readings.
   *
   * ⚠️ Stamped with the status as well as the id, so a `failed` that a retry later carries
   * to `completed` refreshes a second time. Guarded by a ref rather than by the effect's
   * deps because `refreshHistory` clears state, which would re-run the effect it was
   * triggered from.
   */
  const settledStamp = useRef<string | null>(null)
  useEffect(() => {
    if (!flowState?.receipt?.terminal) return
    const stamp = `${flowState.requestId}:${flowState.receipt.status}`
    if (settledStamp.current === stamp) return
    settledStamp.current = stamp
    refreshHistory(flowState.workspaceId)
  }, [flowState, refreshHistory])

  if (!workspaceId || !customerId) return null

  // Offered only to a role the server will accept; no row here means no role (MAR-250).
  if (!role || !ERASURE_ROLES.has(role)) return null

  const who = emailMasked || `record ${customerId}`

  /**
   * ⚠️ **A terminal `failed` must never be a dead end, and this is what guarantees it.**
   *
   * `failed` is three situations (see `readingOf`) and only one of them retries. Two do
   * not: the door threw before dispatching, so no job exists; or the async invoke's
   * `maximum_retry_attempts = 2` are spent. The second is **not reliably discriminable**,
   * so the control cannot be offered on a condition that tries to name it. It is offered on
   * *any* terminal `failed` instead, which covers the case we cannot see.
   *
   * Firing again is safe and is not a duplicate: `findInFlight` skips terminal receipts, so
   * the server accepts a new request, and `redactCustomer` is idempotent — a second run
   * destroys nothing new. Without this, a `failed` receipt left the merchant reading
   * "check again" forever against a job nothing was going to pick up, with a published
   * floor claim quietly unmet.
   */
  const stuck = flow?.receipt?.status === 'failed'
  const canStart = !flow || stuck

  return (
    <div data-testid="kn-erase">
      {flow && (
        <ErasureResult
          flow={flow}
          onRecheck={() => watch(flow.workspaceId, flow.customerId, flow.requestId)}
        />
      )}

      {canStart && (
        <Button variant="ghost" size="sm" onClick={() => setArmedFor(key)}>
          {stuck ? 'Try forgetting her again' : 'Forget this person entirely'}
        </Button>
      )}

      {/* The second act. Open only while armed for THIS record (scoped at the read), and
          only while starting is possible at all. */}
      <ConfirmDialog
        open={canStart && armed}
        title="Forget this person entirely?"
        description={`This cannot be undone. Marva will delete every observation, every inference and every conversation she holds for ${who} — including anything she would have used to recognise them next time.`}
        confirmLabel={`Erase everything she holds on ${who}`}
        busy={sending}
        onConfirm={() => { void fire() }}
        onCancel={() => setArmedFor(null)}
      />

      {note && (
        <Alert tone="danger" data-testid="kn-erase-note">
          {note.text}
        </Alert>
      )}

      {/* ⚠️ The 409 is the one refusal with somewhere to go: the erasure the merchant asked
          for is already running, and its id is in the refusal. Offering it is the whole
          difference between "no" and "it is already happening, here it is". */}
      {offer && (
        <Button
          variant="link"
          onClick={() => watch(offer.workspaceId, offer.customerId, offer.requestId)}
        >
          Show the one that is already running
        </Button>
      )}

      <div data-testid="kn-hist">
        <Button
          variant="link"
          aria-expanded={historyOpen}
          onClick={() => {
            if (historyOpen) { setHistoryOpenFor(null); return }
            setHistoryOpenFor(workspaceId)
            // ⚠️ **Re-read on EVERY open, and drop what is held first.** A cached list is
            // wrong twice over here. It goes stale the moment the merchant erases somebody
            // — and a stale list that ends "that is every erasure this workspace has run"
            // is a FALSE COMPLETENESS CLAIM on an audit trail, which is worse than an old
            // list. And a cached refusal is non-null, so gating the fetch on "do we hold
            // anything" meant a load that failed once never retried until remount. Clearing
            // first costs a "reading…" line and buys a list that is true when it is read.
            setHistoryState(null)
            void loadHistory(workspaceId)
          }}
        >
          {historyOpen ? 'Hide erasures in this workspace' : 'Erasures in this workspace'}
        </Button>
        {historyOpen && <ErasureHistory history={history} />}
      </div>
    </div>
  )
}

/**
 * What came back — the six readings, and never a seventh invented here.
 *
 * ⚠️ The reading is decided by `readingOf`, not by a status string compared in JSX. The two
 * mistakes this surface can make — `needs_review` rendered as a failure, `failed` rendered
 * as done — are both one `===` away in a template, and both break a published claim.
 */
function ErasureResult({ flow, onRecheck }: { flow: Flow; onRecheck: () => void }) {
  const receipt = flow.receipt
  if (!receipt) {
    // Accepted, nothing reported yet. Deliberately NOT "Erased" — this is the exact moment
    // the design's one toast fires, and there is nothing yet to claim.
    const copy = copyFor(flow.unwatched ? 'unwatched' : 'working', null)
    return (
      <Card padding="sm" data-testid="kn-res" data-reading={flow.unwatched ? 'unwatched' : 'working'}>
        <CardHeader titleAs="p" title={copy.headline} description={copy.lead} />
        {flow.pollRefusal && <Alert tone="danger">{flow.pollRefusal}</Alert>}
        {flow.unwatched && (
          <CardFooter>
            <Button variant="ghost" size="sm" onClick={onRecheck}>Check again</Button>
          </CardFooter>
        )}
        <Meta as="p" data-testid="kn-request">Request {flow.requestId}</Meta>
      </Card>
    )
  }

  const reading = readingOf(receipt, flow.unwatched)
  const copy = copyFor(reading, receipt.customerRef)
  const notes = reading === 'forgotten' ? [] : withheldNotes(receipt)
  // `retrying` and `unwatched` are the two readings with something left to happen.
  // ⚠️ `not_started` is deliberately NOT re-checkable: no job was ever dispatched, so there
  // is nothing on the other end to check. Offering it would be a button that can only ever
  // return the same answer. The way out of that one is the erase control, which the parent
  // returns for every terminal `failed`.
  const recheckable = reading === 'retrying' || reading === 'unwatched' || reading === 'working'

  return (
    <Card padding="sm" data-testid="kn-res" data-reading={reading}>
      <CardHeader titleAs="p" title={copy.headline} description={copy.lead} />

      {notes.length > 0 && (
        <div data-testid="kn-ev">
          <Eyebrow tone="muted">{reading === 'retrying' ? 'What broke' : 'What she could not account for'}</Eyebrow>
          <List>{notes.map((n, i) => <li key={i}>{n}</li>)}</List>
        </div>
      )}

      {/* An operator's record exists. Said as a fact, never as an action for the merchant —
          and never with the S3 key, which `toReceiptView` deliberately drops. */}
      {receipt.hasManualReviewRecord && (
        <p>A record of exactly what she set aside is with us.</p>
      )}

      {flow.pollRefusal && (
        <Alert tone="danger">{flow.pollRefusal}</Alert>
      )}

      {recheckable && (
        <CardFooter>
          <Button variant="ghost" size="sm" onClick={onRecheck}>Check again</Button>
        </CardFooter>
      )}

      <Meta as="p" data-testid="kn-request">
        {[
          `Request ${receipt.requestId}`,
          when(receipt.completedAt || receipt.requestedAt),
          (receipt.attempts || 1) > 1 ? `attempt ${receipt.attempts}` : null,
        ].filter(Boolean).join(' · ')}
      </Meta>
    </Card>
  )
}

/**
 * The workspace's erasures.
 *
 * ⚠️ The Receipt product test asks whether a merchant can read what Marva did and why. The
 * v6 design has no receipt view of any kind despite `GET …/erasures` existing, so this is
 * ours — kept modest, and stating `truncated` rather than implying completeness.
 *
 * ⚠️ **Rendered in the order given, and never re-sorted here.** `listErasureReceipts`
 * already sorts by `requestedAt`, newest first, deliberately — *"because the key sorts by
 * request id and the id is not chronological"*. A second sort on this side is a second copy
 * of an ordering rule, which is how two copies come to disagree.
 *
 * ⚠️ **But a TRUNCATED page is newest-first over an ARBITRARY subset.** The query reads
 * `begins_with(configType, 'erasure#')` in key order — `erasure#<uuid>`, not chronological
 * — and sorts the window it happened to read. So the newest erasure in the workspace need
 * not be in a shortened list at all. `findInFlight` reads the whole history for exactly
 * this reason. Calling a short list "the latest" would be a claim the key space does not
 * support, and the copy below says so instead.
 */
function ErasureHistory({ history }: { history: History | null }) {
  if (!history) return <LoadingState caption="Reading this workspace's erasures…" />
  if (history.refusal) return <ErrorState title={history.refusal} />
  if (history.receipts.length === 0) {
    return <EmptyState size="sm">No one has been erased in this workspace.</EmptyState>
  }
  return (
    <>
      <List data-testid="kn-hist-list">
        {history.receipts.map((r) => (
          <li key={r.requestId} data-reading={readingOf(r)}>
            <span data-field="word">{historyWord(r)}</span>
            {' · '}
            <Meta data-field="customer">{r.customerRef || r.customerId}</Meta>
            {' · '}
            <Meta data-field="when">{when(r.completedAt || r.requestedAt)}</Meta>
          </li>
        ))}
      </List>
      <p data-testid="kn-hist-claim">
        {history.truncated === true
          ? `There are more erasures than these ${history.receipts.length}, and a shortened list is not necessarily the most recent ones — receipts are kept under their own id rather than by date.`
          : history.truncated === false
            // ⚠️ The ONLY branch that claims completeness, and it is the server's claim
            // being repeated, not the page's own.
            ? 'That is every erasure this workspace has run.'
            : 'Marva did not say whether this is all of them, so read it as a partial list.'}
      </p>
    </>
  )
}
